import { Music2, Play, ListMusic } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type Playlist } from '@/lib/musicTypes';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { useTrackZaps, formatSats } from '@/hooks/useTrackZaps';
import { ZapButton } from './ZapButton';
import { LikeButton } from './LikeButton';
import { usePlaylistTracks } from '@/hooks/usePlaylistTracks';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';

interface PlaylistCardProps {
  playlist: Playlist;
  onUpdate?: () => void;
}

export function PlaylistCard({ playlist }: PlaylistCardProps) {
  const author = useAuthor(playlist.pubkey);
  const { data: zaps } = useTrackZaps(playlist.event.kind, playlist.pubkey, playlist.id);
  const { data: tracks } = usePlaylistTracks(playlist);
  const { playTrack } = useMusicPlayer();

  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(playlist.pubkey);

  // Calculate total zap amount
  const totalZaps = zaps?.reduce((sum, zap) => sum + zap.amount, 0) || 0;

  // Create naddr for the playlist
  const naddr = nip19.naddrEncode({
    kind: playlist.event.kind,
    pubkey: playlist.pubkey,
    identifier: playlist.id,
  });

  // Generate gradient background if no image
  const backgroundStyle = playlist.imageUrl
    ? { backgroundImage: `url(${playlist.imageUrl})` }
    : playlist.gradient
    ? { background: `linear-gradient(135deg, ${playlist.gradient.join(', ')})` }
    : { background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.6))' };

  const handleCardClick = () => {
    // Navigation is handled by the Link component
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (tracks && tracks.length > 0) {
      playTrack(tracks[0], tracks);
    }
  };

  return (
    <Link to={`/${naddr}`} className="block">
      <Card className="group overflow-hidden hover:bg-white/[0.07] transition-all cursor-pointer border-0 bg-transparent">
        <div className="flex items-center gap-4 p-2">
          {/* Playlist Cover - Smaller */}
          <div
            className="relative w-16 h-16 flex-shrink-0 rounded bg-cover bg-center"
            style={backgroundStyle}
          >
            {/* Show icon on gradient backgrounds (no image) */}
            {!playlist.imageUrl && (
              <div className="absolute inset-0 flex items-center justify-center">
                <ListMusic className="h-8 w-8 text-white drop-shadow" />
              </div>
            )}
          </div>

          {/* Playlist Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base line-clamp-1 hover:text-primary transition-colors">
              {playlist.title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-1">
              {displayName} • {playlist.trackRefs.length} track{playlist.trackRefs.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Zap Total Badge */}
            {totalZaps > 0 && (
              <Badge variant="secondary" className="text-xs font-semibold">
                {formatSats(totalZaps)}
              </Badge>
            )}

            {/* Like Button */}
            <div
              className="flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <LikeButton
                event={playlist.event}
                size="icon"
                variant="ghost"
                className="bg-white/[0.07] hover:bg-white/[0.12]"
              />
            </div>

            {/* Zap Button */}
            <div
              className="flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <ZapButton
                event={playlist.event}
                size="icon"
                variant="ghost"
                className="rounded-full bg-white/[0.07] hover:bg-white/[0.12]"
              />
            </div>

            {/* Play Button */}
            <Button
              size="icon"
              variant="ghost"
              onClick={handlePlayClick}
              disabled={!tracks || tracks.length === 0}
              className="h-10 w-10 rounded-full flex-shrink-0 border-0 bg-white text-black hover:bg-white/90 disabled:opacity-30 [&_svg]:!text-black"
            >
              <Play className="h-4 w-4 ml-0.5" />
            </Button>
          </div>
        </div>
      </Card>
    </Link>
  );
}
